/* Inline quick-shop options on collection cards.
   Maps colour-swatch + dropdown selections to a variant id, updates the
   hidden [name=id], the add button, AND the card photo — so changing the
   colour on a collection card shows that colour, same as the product page.
   The surrounding Dawn <product-form> handles the AJAX add-to-cart. */
if (!customElements.get('tessera-card-options')) {
  customElements.define(
    'tessera-card-options',
    class TesseraCardOptions extends HTMLElement {
      connectedCallback() {
        const dataEl = document.getElementById(this.dataset.variants);
        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.data = { main: null, variants: [], images: [] };
        }
        this.variants = this.data.variants || [];

        this.form = this.closest('form');
        this.productForm = this.closest('product-form');
        this.idInput = this.form && this.form.querySelector('[name="id"]');
        this.button = this.productForm && this.productForm.querySelector('.tessera-cardopts__add');
        /* The price always sits outside this element - in the parure row head,
           or in Dawn's card block on a collection grid - so it is looked up
           from the nearest enclosing row/card rather than from within. */
        this.priceScope =
          this.closest('.tessera-parure-row') ||
          this.closest('.card__content') ||
          this.closest('.card-wrapper') ||
          this.parentElement;
        this.priceEl = this.priceScope ? this.priceScope.querySelector('[data-variant-price]') : null;
        this.buttonText = this.button && this.button.querySelector('span');

        const card = this.closest('.card-wrapper');
        this.cardImage = card && card.querySelector('.card__media img');
        // The card's own starting photo is the "home" image to return to.
        if (this.cardImage && !this.homeImage) {
          this.homeImage = this.cardImage.getAttribute('src') || this.data.main;
        }

        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);
        this.selectInitialColor();
        this.updateVariant();

        // Preload swap targets on first hover/focus so colour changes are instant.
        if (card) {
          const preload = () => this.preloadImages();
          card.addEventListener('pointerenter', preload, { once: true });
          card.addEventListener('focusin', preload, { once: true });
        }
      }

      preloadImages() {
        if (this._preloaded) return;
        this._preloaded = true;
        const urls = new Set();
        this.variants.forEach((v) => { if (v.image) urls.add(v.image); });
        (this.data.images || []).forEach((img) => { if (img.u) urls.add(img.u); });
        urls.forEach((u) => { const i = new Image(); i.decoding = 'async'; i.src = u; });
      }

      /* Pre-select the colour the MAIN photo actually shows, so the card opens
         on the main picture — matching the product page. */
      selectInitialColor() {
        const radios = [...this.querySelectorAll('.tessera-cardopts__colors input')];
        if (radios.length < 2) return;

        const firstAlt = (this.data.images && this.data.images[0] && this.data.images[0].a) || '';
        const hay = ((this.data.main || '') + ' ' + firstAlt).toLowerCase();

        // longest names first so "Baby Blue" wins over "Blue"
        const sorted = radios.slice().sort((a, b) => b.value.length - a.value.length);
        let target = null;

        // Same reverse lookup the product page does: a curated entry pins a
        // colour to a filename, which is the only way to read the colour off
        // photos named "1_63.png".
        const cmap = window.TESSERA_COLOR_MAP && window.TESSERA_COLOR_MAP[this.dataset.handle];
        if (cmap) {
          target = radios.find((r) => {
            const frag = cmap[r.value.toLowerCase().trim()];
            return frag && hay.indexOf(frag.toLowerCase()) !== -1;
          }) || null;
        }

        for (const r of sorted) {
          if (target) break;
          const base = r.value.toLowerCase().trim();
          const cands = [base, base.replace(/\s+/g, '-'), base.replace(/\s+/g, '_'), base.replace(/\s+/g, '')];
          if (cands.some((c) => c && hay.indexOf(c) !== -1)) {
            target = r;
            break;
          }
        }

        // main photo reads "white" but the option is Ivory / Off White / Cream
        if (!target && hay.indexOf('white') !== -1) {
          const family = ['offwhite', 'ivory', 'cream', 'ecru', 'natural', 'white'];
          const matches = radios.filter((r) =>
            family.indexOf(r.value.toLowerCase().replace(/[\s_-]/g, '')) !== -1
          );
          if (matches.length === 1) target = matches[0];
        }

        if (target && !target.checked) target.checked = true;
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      selectedValues() {
        const values = [];
        this.querySelectorAll('[data-position]').forEach((el) => {
          if (el.tagName === 'SELECT') {
            values[parseInt(el.dataset.position, 10) - 1] = el.value;
          } else if (el.tagName === 'INPUT' && el.checked) {
            values[parseInt(el.dataset.position, 10) - 1] = el.value;
          }
        });
        return values;
      }

      selectedColor() {
        let color = null;
        this.querySelectorAll('.tessera-cardopts__colors input:checked').forEach((input) => {
          color = input.value;
        });
        return color;
      }

      matchVariant(values) {
        return this.variants.find((v) =>
          v.options.every((opt, i) => values[i] === undefined || opt === values[i])
        );
      }

      /* A deliberate choice from tessera-color-map.js: this handle, this
         colour, this photo. Nothing outranks it. */
      curatedImageByColor(color) {
        if (!color) return null;
        const base = color.toLowerCase().trim();
        const map = window.TESSERA_COLOR_MAP && window.TESSERA_COLOR_MAP[this.dataset.handle];
        if (!map || !map[base]) return null;
        const frag = map[base].toLowerCase();
        const mapped = (this.data.images || []).find((img) => (img.u || '').toLowerCase().indexOf(frag) !== -1);
        return mapped ? mapped.u : null;
      }

      /* Last resort: a photo whose filename or alt text mentions the colour. */
      guessImageByColor(color) {
        if (!color) return null;
        const base = color.toLowerCase().trim();
        const candidates = [base, base.replace(/\s+/g, '-'), base.replace(/\s+/g, '_'), base.replace(/\s+/g, '')];
        const hit = (this.data.images || []).find((img) => {
          const hay = ((img.u || '') + ' ' + (img.a || '')).toLowerCase();
          return candidates.some((c) => c && hay.indexOf(c) !== -1);
        });
        return hit ? hit.u : null;
      }

      matchImageByColor(color) {
        return this.curatedImageByColor(color) || this.guessImageByColor(color);
      }

      updateCardImage(variant) {
        if (!this.cardImage) return;
        const color = this.selectedColor();
        // A curated choice outranks the variant photo. Basic Line is why: every
        // one of its products carries the same thirty photos, so each variant's
        // own image is the same bed shot and all five rows looked identical.
        // Below that, the variant image is still the most reliable signal.
        const url =
          this.curatedImageByColor(color) ||
          (variant && variant.image) ||
          this.guessImageByColor(color) ||
          this.homeImage ||
          this.data.main;
        if (!url) return;
        if (this.cardImage.getAttribute('src') === url) return;
        // srcset would override src, so drop it before swapping.
        this.cardImage.removeAttribute('srcset');
        this.cardImage.removeAttribute('sizes');
        this.cardImage.setAttribute('src', url);
      }

      onChange() {
        this.updateVariant();
      }

      /* Values arrive preformatted from Liquid's money filter, so nothing is
         reformatted here and the shop's currency format and active locale are
         both respected. A variant with no price leaves the last good value
         alone rather than blanking it. */
      updatePrice(variant) {
        if (!variant || !variant.price) return;

        // Parure rows carry their own simple price element.
        if (this.priceEl) this.priceEl.textContent = variant.price;

        /* Collection-grid cards use Dawn's price block instead. Towels are why
           this matters: a Stockholm hand towel is LE 54.90 and the full bundle
           is LE 1,485.90, and the card showed the hand-towel price whatever the
           customer picked. */
        const scope = this.priceScope;
        if (!scope) return;
        const root = scope.querySelector('.price');
        if (!root) return;

        const onSale = Boolean(variant.compareAt);
        root.classList.toggle('price--on-sale', onSale);

        const regular = scope.querySelector('.price__regular .price-item--regular');
        if (regular) regular.textContent = variant.price;

        // The sale block shows the struck compare-at price beside the new one.
        const was = scope.querySelector('.price__sale s.price-item--regular');
        if (was) was.textContent = variant.compareAt || '';
        const sale = scope.querySelector('.price-item--sale');
        if (sale) sale.textContent = variant.price;
      }

      updateVariant() {
        const variant = this.matchVariant(this.selectedValues());
        this.updateCardImage(variant);
        this.updatePrice(variant);

        if (!this.idInput || !this.button) return;
        const strings = window.variantStrings || {};

        if (!variant) {
          this.idInput.value = '';
          this.button.setAttribute('disabled', 'disabled');
          if (this.buttonText) this.buttonText.textContent = strings.unavailable || 'Unavailable';
          return;
        }

        this.idInput.value = variant.id;
        this.idInput.removeAttribute('disabled');

        if (variant.available) {
          this.button.removeAttribute('disabled');
          if (this.buttonText) this.buttonText.textContent = 'Add to basket';
        } else {
          this.button.setAttribute('disabled', 'disabled');
          if (this.buttonText) this.buttonText.textContent = strings.soldOut || 'Sold out';
        }
      }
    }
  );
}

/* ---------- quantity steppers ----------

   <tessera-card-options> wraps only the colour swatches; the quantity control
   is a sibling outside it, so the element's own listeners never see it. One
   delegated listener on the document covers every card, including any rendered
   later by the collection grid or the cart drawer. */
(function () {
  function clamp(input, next) {
    var min = parseInt(input.min, 10) || 1;
    var max = parseInt(input.max, 10);
    if (next < min) next = min;
    if (!isNaN(max) && next > max) next = max;
    return next;
  }

  function sync(wrap) {
    var input = wrap.querySelector('[data-qty-input]');
    if (!input) return;
    var value = parseInt(input.value, 10);
    var min = parseInt(input.min, 10) || 1;
    var max = parseInt(input.max, 10);
    if (isNaN(value)) value = min;
    wrap.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      var step = parseInt(btn.dataset.qtyStep, 10) || 0;
      btn.disabled = step < 0 ? value <= min : !isNaN(max) && value >= max;
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-qty-step]') : null;
    if (!btn) return;
    var wrap = btn.closest('[data-qty-wrap]');
    var input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;

    var min = parseInt(input.min, 10) || 1;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = min;
    input.value = clamp(input, current + (parseInt(btn.dataset.qtyStep, 10) || 0));

    /* Let anything bound to the field (validation, price displays) react as if
       the customer had typed the number. */
    input.dispatchEvent(new Event('change', { bubbles: true }));
    sync(wrap);
  });

  /* Typing into the field has to update the buttons too. */
  document.addEventListener('input', function (event) {
    var input = event.target.closest ? event.target.closest('[data-qty-input]') : null;
    if (input) sync(input.closest('[data-qty-wrap]'));
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-qty-wrap]').forEach(sync);
  });
})();
